import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { usePlano, PLANOS_CONFIG } from '../hooks/usePlano'
import { supabase, profileService } from '../lib/supabase'
import toast from 'react-hot-toast'

// ─── Lista completa de instrumentos (igual ao AuthPage) ───────────────────────
const INSTRUMENTOS_GRUPOS = {
  'Cordas / Harmonia': ['Violão', 'Guitarra', 'Baixo', 'Teclado', 'Piano', 'Cavaquinho', 'Ukulele'],
  'Percussão':         ['Bateria', 'Percussão'],
  'Madeiras':          ['Flauta', 'Flauta Piccolo', 'Oboé', 'Corne Inglês', 'Clarinete', 'Clarinete Alto', 'Clarinete Baixo', 'Fagote', 'Contrafagote', 'Saxofone Soprano', 'Saxofone Alto', 'Saxofone Tenor', 'Saxofone Barítono', 'Saxofone Baixo'],
  'Metais':            ['Trompa', 'Trompete', 'Corneta', 'Trombone', 'Trombone Alto', 'Trombone Tenor', 'Trombone Baixo', 'Eufônio', 'Bombardino', 'Tuba', 'Tuba Baixo'],
  'Cordas Clássicas':  ['Violino', 'Viola', 'Violoncelo', 'Contrabaixo'],
  'Outros':            ['Voz', 'Outro'],
}
const TODOS = Object.values(INSTRUMENTOS_GRUPOS).flat()

export default function PerfilPage() {
  const navigate = useNavigate()
  const { user, profile, atualizarPerfil } = useAuth()
  const { plano, config: planoCfg } = usePlano()

  // Dados editáveis do perfil
  const [nome, setNome]       = useState('')
  const [apelido, setApelido] = useState('')

  // Instrumentos: lista e ativo
  const [meus, setMeus]           = useState([])   // array de strings
  const [ativo, setAtivo]         = useState('')    // profile.instrumento
  const [showPicker, setShowPicker] = useState(false)

  const [modoVocal, setModoVocal] = useState(false)
  const [salvando, setSalvando]   = useState(false)
  const [upAvatar, setUpAvatar]   = useState(false)

  // Inicializa campos com dados do perfil
  useEffect(() => {
    if (!profile) return
    setNome(profile.nome || '')
    setApelido(profile.apelido || '')
    setAtivo(profile.instrumento || '')
    setModoVocal(profile.modo_vocal || false)
    // instrumentos: array salvo ou fallback para o instrumento único do cadastro
    const lista = profile.instrumentos?.length
      ? profile.instrumentos
      : profile.instrumento ? [profile.instrumento] : []
    setMeus(lista)
  }, [profile])

  // ── Salva nome/apelido ────────────────────────────────────────────────────
  async function salvarPerfil() {
    setSalvando(true)
    try {
      await atualizarPerfil({ nome: nome.trim(), apelido: apelido.trim(), modo_vocal: modoVocal })
      toast.success('Perfil atualizado!')
    } catch (e) {
      toast.error('Erro: ' + e.message)
    } finally {
      setSalvando(false)
    }
  }

  async function toggleModoVocal() {
    const novo = !modoVocal
    setModoVocal(novo)
    try {
      await atualizarPerfil({ modo_vocal: novo })
      toast.success(novo ? '🎤 Modo vocal ativado' : 'Modo vocal desativado')
    } catch {
      setModoVocal(!novo)
      toast.error('Erro ao salvar preferência')
    }
  }

  // ── Adiciona instrumento à lista ──────────────────────────────────────────
  function adicionarInstrumento(inst) {
    if (meus.includes(inst)) { toast('Instrumento já adicionado'); return }
    const nova = [...meus, inst]
    setMeus(nova)
    salvarInstrumentos(nova, ativo || inst)
    if (!ativo) setAtivo(inst) // define como ativo se era o primeiro
    setShowPicker(false)
    toast.success(`${inst} adicionado!`)
  }

  // ── Remove instrumento da lista ───────────────────────────────────────────
  function removerInstrumento(inst) {
    if (inst === ativo && meus.length > 1) {
      toast.error('Defina outro como ativo antes de remover este.')
      return
    }
    const nova = meus.filter(i => i !== inst)
    const novoAtivo = inst === ativo ? (nova[0] || '') : ativo
    setMeus(nova)
    setAtivo(novoAtivo)
    salvarInstrumentos(nova, novoAtivo)
    toast.success(`${inst} removido`)
  }

  // ── Define instrumento ativo ──────────────────────────────────────────────
  function definirAtivo(inst) {
    setAtivo(inst)
    salvarInstrumentos(meus, inst)
    toast.success(`${inst} definido como ativo`)
  }

  // ── Persiste no Supabase ──────────────────────────────────────────────────
  async function salvarInstrumentos(lista, ativoVal) {
    try {
      await atualizarPerfil({ instrumento: ativoVal, instrumentos: lista })
    } catch (e) {
      toast.error('Erro ao salvar instrumento')
    }
  }

  // ── Upload de avatar ──────────────────────────────────────────────────────
  async function handleAvatar(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUpAvatar(true)
    try {
      const url = await profileService.uploadAvatar(user.id, file)
      await atualizarPerfil({ avatar_url: url })
      toast.success('Foto atualizada!')
    } catch (e) {
      toast.error('Erro ao enviar foto')
    } finally {
      setUpAvatar(false)
    }
  }

  const iniciais = (n) => n?.split(' ').map(p => p[0]).join('').substring(0, 2).toUpperCase() || '?'

  return (
    <div className="app-container" style={{ background: 'var(--brand)' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, cursor: 'pointer' }}>‹</button>
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 18, fontWeight: 700, flex: 1 }}>Meu Perfil</div>
        <button className="btn" style={{ fontSize: 12, padding: '7px 14px' }} onClick={salvarPerfil} disabled={salvando}>
          {salvando ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Salvar'}
        </button>
      </div>

      <div className="page-content" style={{ padding: '20px 20px', paddingBottom: 40 }}>

        {/* Avatar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <label htmlFor="avatar-input" style={{ cursor: 'pointer', position: 'relative' }}>
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="avatar"
                style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--brand2)' }} />
            ) : (
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--brand2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, color: '#fff', border: '2px solid var(--brand2)' }}>
                {iniciais(nome)}
              </div>
            )}
            <div style={{ position: 'absolute', bottom: 0, right: 0, width: 22, height: 22, borderRadius: '50%', background: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
              {upAvatar ? '…' : '📷'}
            </div>
            <input id="avatar-input" type="file" accept="image/*" onChange={handleAvatar} style={{ display: 'none' }} />
          </label>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{nome || 'Seu nome'}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{user?.email}</div>
          </div>
        </div>

        {/* Card plano atual */}
        <div onClick={() => navigate('/planos')} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${planoCfg?.cor || 'var(--card-border)'}33`, borderRadius: 14, marginBottom: 20, cursor: 'pointer' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${planoCfg?.cor || '#fff'}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
            {plano === 'free' ? '🎵' : plano === 'musico' ? '🎸' : plano === 'pro' ? '⚡' : '🎼'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 2 }}>Plano atual</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: planoCfg?.cor || 'var(--text)' }}>
              {planoCfg?.nome || 'Gratuito'}
              {planoCfg?.preco > 0 && <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--muted)', marginLeft: 6 }}>R${planoCfg.preco.toFixed(2).replace('.', ',')}/mês</span>}
            </div>
          </div>
          <div style={{ fontSize: 12, color: plano === 'free' ? 'var(--gold)' : 'var(--muted)', fontWeight: plano === 'free' ? 700 : 400 }}>
            {plano === 'free' ? 'Assinar ›' : 'Gerenciar ›'}
          </div>
        </div>

        {/* Dados pessoais */}
        <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--card-border)', borderRadius: 14, padding: '16px', marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Dados pessoais</div>

          <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Nome completo</label>
          <input value={nome} onChange={e => setNome(e.target.value)} className="input" placeholder="Seu nome" style={{ marginBottom: 12 }} />

          <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Apelido (opcional)</label>
          <input value={apelido} onChange={e => setApelido(e.target.value)} className="input" placeholder="Ex: Zé Violão" />
        </div>

        {/* Modo Vocal */}
        <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--card-border)', borderRadius: 14, padding: '16px', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>🎤 Modo vocal</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 }}>
                Oculta linhas de acordes — exibe apenas a letra
              </div>
            </div>
            <button onClick={toggleModoVocal} style={{
              width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', position: 'relative', flexShrink: 0,
              background: modoVocal ? 'var(--brand2)' : 'rgba(255,255,255,0.15)',
              transition: 'background 0.2s'
            }}>
              <div style={{
                position: 'absolute', top: 3, left: modoVocal ? 25 : 3, width: 20, height: 20,
                borderRadius: '50%', background: '#fff', transition: 'left 0.2s'
              }} />
            </button>
          </div>
          {(modoVocal || ativo === 'Voz') && (
            <div style={{ fontSize: 11, color: 'var(--brand2)', marginTop: 10, padding: '6px 10px', background: 'rgba(0,207,255,0.06)', borderRadius: 8 }}>
              {ativo === 'Voz' && !modoVocal ? '🎤 Ativado automaticamente (instrumento: Voz)' : '✓ Ativo — você verá apenas a letra nas cifras'}
            </div>
          )}
        </div>

        {/* Instrumentos */}
        <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--card-border)', borderRadius: 14, padding: '16px', marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Meus instrumentos</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
            O instrumento <strong style={{ color: 'var(--brand2)' }}>ativo</strong> é usado no CifrasBand Classic para exibir sua partitura automaticamente.
          </div>

          {/* Lista de instrumentos cadastrados */}
          {meus.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', padding: '12px 0' }}>
              Nenhum instrumento cadastrado
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
              {meus.map(inst => {
                const eAtivo = inst === ativo
                return (
                  <div key={inst} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: eAtivo ? 'rgba(0,207,255,0.08)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${eAtivo ? 'rgba(0,207,255,0.3)' : 'var(--card-border)'}`,
                    borderRadius: 10, padding: '10px 14px'
                  }}>
                    {/* Ativo indicator */}
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: eAtivo ? 'var(--brand2)' : 'rgba(255,255,255,0.15)', flexShrink: 0, boxShadow: eAtivo ? '0 0 6px var(--brand2)' : 'none' }} />

                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: eAtivo ? 700 : 400, fontSize: 14 }}>{inst}</div>
                      {eAtivo && <div style={{ fontSize: 10, color: 'var(--brand2)', marginTop: 1 }}>✓ Ativo — usado no Classic e no perfil</div>}
                    </div>

                    {/* Botão definir ativo */}
                    {!eAtivo && (
                      <button onClick={() => definirAtivo(inst)}
                        style={{ fontSize: 11, padding: '4px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.08)', border: '1px solid var(--card-border)', color: 'var(--muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        Usar este
                      </button>
                    )}

                    {/* Botão remover */}
                    <button onClick={() => removerInstrumento(inst)}
                      style={{ background: 'none', border: 'none', color: eAtivo && meus.length === 1 ? 'rgba(255,107,107,0.3)' : '#ff6b6b', fontSize: 16, cursor: 'pointer', padding: '0 4px' }}
                      title="Remover">✕</button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Botão adicionar */}
          <button onClick={() => setShowPicker(true)}
            style={{ width: '100%', padding: '10px', borderRadius: 10, border: '1px dashed rgba(0,207,255,0.3)', background: 'rgba(0,207,255,0.05)', color: 'var(--brand2)', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
            + Adicionar instrumento
          </button>
        </div>

        {/* Info */}
        <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', lineHeight: 1.6 }}>
          Você pode ter quantos instrumentos quiser registrados.{'\n'}
          Apenas o <strong>ativo</strong> aparece no seu perfil e é usado no Classic.
        </div>
      </div>

      {/* Modal picker de instrumento */}
      {showPicker && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-end', zIndex: 200 }}>
          <div style={{ width: '100%', maxWidth: 480, margin: '0 auto', background: 'var(--card)', borderRadius: '20px 20px 0 0', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            {/* Handle */}
            <div style={{ padding: '16px 20px 8px', borderBottom: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 16 }}>Escolher instrumento</div>
              <button onClick={() => setShowPicker(false)}
                style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>

            <div style={{ overflowY: 'auto', padding: '12px 20px 32px' }}>
              {Object.entries(INSTRUMENTOS_GRUPOS).map(([grupo, itens]) => (
                <div key={grupo} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 10, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{grupo}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {itens.map(inst => {
                      const jaTem = meus.includes(inst)
                      return (
                        <button key={inst} onClick={() => !jaTem && adicionarInstrumento(inst)}
                          style={{
                            padding: '7px 14px', borderRadius: 20, fontSize: 13, cursor: jaTem ? 'default' : 'pointer',
                            border: 'none',
                            background: jaTem ? 'rgba(0,207,255,0.15)' : 'rgba(255,255,255,0.07)',
                            color: jaTem ? 'var(--brand2)' : 'var(--text)',
                            fontWeight: jaTem ? 700 : 400,
                            opacity: jaTem ? 0.7 : 1
                          }}>
                          {inst} {jaTem ? '✓' : ''}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
